import React, { useRef, useState } from 'react'
import type { PopperChildrenProps, ReferenceChildrenProps } from 'react-popper'
import { Manager, Popper, Reference } from 'react-popper'

import {
  Button,
  ButtonClean,
  ButtonGroup,
  Fieldset,
  ModalBox,
  Tag,
  useMediaQueryContext,
  useModalContext,
  useOutsideClick,
} from '@fourthwall/components'
import { ArrowShortDownIcon } from '@fourthwall/icons'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { TimezonedCalendar } from '@modules/Timezone/components/TimezonedCalendar'
import { TimezonedDatePicker } from '@modules/Timezone/components/TimezonedDatePicker'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'

import * as S from './ScheduleTag.styled'
import type { Props } from './types'

type ScheduleModalProps = {
  date: Date
  onDateUpdate: (date: Date) => void
  onPublishNow: () => void
}

export const ScheduleModal = ({ date, onDateUpdate, onPublishNow }: ScheduleModalProps) => {
  const [innerDate, setInnerDate] = useState(date)
  const { close } = useModalContext()

  return (
    <ModalBox title="Publish date & time" onClose={close}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <TimezonedCalendar
          type="single"
          selectedDates={[innerDate]}
          onDatesChange={([date]) => setInnerDate(date)}
          timeLabels={['Publish time']}
          minDate={new Date()}
        />
        <Button
          label="Save"
          appearance="primary"
          onClick={() => {
            onDateUpdate(innerDate)
            close()
          }}
        />
        <Button
          label="Publish now"
          appearance="secondary"
          onClick={() => {
            onPublishNow()
            close()
          }}
        />
        <Button label="Cancel" appearance="secondary" onClick={close} />
      </div>
    </ModalBox>
  )
}

export const ScheduleTag = ({ currentDate, onDateUpdate }: Props) => {
  const [isOpen, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const modal = useModalContext()
  const media = useMediaQueryContext()
  useOutsideClick(containerRef, () => {
    setOpen(false)
  })

  const toggleOpen = () => {
    setOpen((prevIsOpen) => !prevIsOpen)
  }

  const formik = useFormik<{ date: Date }>({
    initialValues: {
      date: currentDate,
    },
    onSubmit: (values) => {
      const liveAt = currentDate === values.date ? new Date() : values.date

      onDateUpdate(liveAt)
      setOpen(false)
    },
    enableReinitialize: true,
  })

  const renderReference = ({ ref }: ReferenceChildrenProps) => {
    return (
      <button ref={ref} type="button" onClick={toggleOpen} data-testid="ScheduleTag.button">
        <Tag
          label={
            <>
              Goes public <TimezonedDateString date={currentDate} format="MMM D" />
            </>
          }
          appearance="alert"
          size="large"
          icon={ArrowShortDownIcon}
        />
      </button>
    )
  }

  const renderTooltip = ({ ref, style }: PopperChildrenProps) => (
    <div ref={ref} style={{ ...style, paddingTop: 8, zIndex: 5 }}>
      <S.FormContainer>
        <form onSubmit={formik.handleSubmit} data-testid="ScheduleTag.form">
          <Fieldset>
            <Fieldset.Row>
              <TimezonedDatePicker
                value={[formik.values.date]}
                labels={['Publish date & time']}
                dateFormat="MMM D, YYYY, h:mm A"
                minDate={new Date()}
                onChange={([newDate]) => {
                  formik.setFieldValue('date', newDate)
                }}
                showTimeInputs
              />
            </Fieldset.Row>
          </Fieldset>
          <S.ButtonsContainer>
            <ButtonGroup justify="right" align="center" space="medium">
              <ButtonClean
                label="Publish now"
                onClick={() => {
                  formik.setFieldValue('date', new Date())
                  formik.submitForm()
                }}
              />
              <Button
                label="Cancel"
                size="small"
                onClick={() => {
                  formik.resetForm()
                  setOpen(false)
                }}
              />
              <Button
                label="Save"
                appearance="primary"
                disabled={!formik.dirty || !formik.isValid}
                size="small"
                onClick={formik.submitForm}
              />
            </ButtonGroup>
          </S.ButtonsContainer>
        </form>
      </S.FormContainer>
    </div>
  )

  if (media.tablet) {
    return (
      <Manager>
        <div ref={containerRef}>
          <Reference>{renderReference}</Reference>
          {isOpen && <Popper placement="bottom-end">{renderTooltip}</Popper>}
        </div>
      </Manager>
    )
  }

  return (
    <button
      type="button"
      onClick={() =>
        modal.open(ScheduleModal, {
          date: formik.values.date,
          onDateUpdate: (date) => {
            formik.setFieldValue('date', date)
            formik.submitForm()
          },
          onPublishNow: () => {
            formik.setFieldValue('date', new Date())
            formik.submitForm()
          },
        })
      }
    >
      <Tag
        label={
          <>
            Goes public <TimezonedDateString date={currentDate} format="MMM D" />
          </>
        }
        appearance="alert"
        size="large"
        icon={ArrowShortDownIcon}
      />
    </button>
  )
}
